// TopBar (DESIGN §3.1.3): links großer Seitentitel (h1) oder Zurück-Knopf + Titel bzw.
// Breadcrumbs, rechts Icon-Knöpfe in fester Reihenfolge. Klebt oben, Linie erst ab 8 px Scroll.
// Den Titel meldet die Seite über PageHeader/usePageTitle; ohne Seitentitel gilt `fallbackTitle`.
import { type ReactNode } from 'react';
import { cn } from '../kern/cn';
import { type BackTarget, usePageChrome } from '../kern/page-chrome';
import { BackButton } from '../kern/PageHeader';
import { useScrolled } from './parts';

export interface TopBarProps {
  /** Fester Titel; ohne Angabe der Titel der Seite, sonst `fallbackTitle`. */
  title?: ReactNode;
  fallbackTitle?: ReactNode;
  back?: BackTarget;
  /** Statt Titel (Admin-Detailseiten). */
  breadcrumbs?: ReactNode;
  /** Links vor dem Titel, z. B. Menü-Knopf (Admin/AI mobil). */
  leading?: ReactNode;
  /** Rechts: Suche, Chat, Glocke, Avatar … */
  actions?: ReactNode;
  /** Status-Chip neben den Aktionen („Reconnecting…“). */
  status?: ReactNode;
  /** `people`: 68/56 mit großem Titel; `compact`: 56 mit kleinem Titel (Bar AI). */
  size?: 'people' | 'compact';
  className?: string;
}

export function TopBar({
  title: titleProp,
  fallbackTitle,
  back: backProp,
  breadcrumbs,
  leading,
  actions,
  status,
  size = 'people',
  className,
}: TopBarProps) {
  const scrolled = useScrolled(8);
  const chrome = usePageChrome();
  const title = titleProp ?? chrome.title?.title ?? fallbackTitle;
  const back = backProp ?? chrome.title?.back;
  return (
    <header
      data-scrolled={scrolled || undefined}
      className={cn(
        'sticky top-0 z-(--bar-z-nav) flex shrink-0 items-center gap-2 border-b border-transparent bg-page transition-[border-color] duration-(--bar-dur-fast) data-scrolled:border-line-subtle',
        size === 'people' ? 'h-topbar-m md:h-topbar' : 'h-topbar-m',
        className,
      )}
    >
      {leading}
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {back ? <BackButton back={back} className="-ml-2" /> : null}
        {breadcrumbs ? (
          <div className="min-w-0 flex-1">{breadcrumbs}</div>
        ) : title ? (
          <h1
            className={cn(
              'min-w-0 truncate text-fg',
              size === 'people' ? 'text-title-page-m md:text-title-page' : 'text-label-strong',
            )}
          >
            {title}
          </h1>
        ) : null}
      </div>
      {status}
      {actions ? <div className="flex shrink-0 items-center gap-0 md:gap-1">{actions}</div> : null}
    </header>
  );
}
